import { useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadSimple, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function ImportEmployees() {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/employees/import/preview", fd);
      setPreview(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const rows = preview.rows.filter((r) => r.valid).map(({ errors, valid, ...r }) => r);
      const { data } = await api.post("/employees/import", { rows });
      toast.success(`${data.inserted} dipendenti importati`);
      setPreview(null);
      setFileName("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="import-page" className="space-y-3 max-w-5xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Importa Dipendenti</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Carica un file Excel (.xlsx) o CSV con le colonne: <b>Nome, Cognome, Codice, Reparto, Mansione</b> (opzionale: Data assunzione).
        </p>
      </div>

      <div className="rounded-sm border border-dashed border-border bg-card p-6 text-center">
        <UploadSimple size={28} className="mx-auto text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">{fileName || "Nessun file selezionato"}</p>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" onChange={onFile} className="hidden" data-testid="import-file-input" />
        <Button className="mt-3 rounded-sm font-semibold" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="import-select-btn">
          Seleziona file
        </Button>
      </div>

      {preview && (
        <div className="rounded-sm border border-border bg-card" data-testid="import-preview">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div className="text-sm">
              <b className="tabular-nums" data-testid="import-total">{preview.total}</b> righe lette ·{" "}
              <b className="text-emerald-600 dark:text-emerald-400 tabular-nums" data-testid="import-valid">{preview.valid}</b> valide ·{" "}
              <b className="text-rose-600 dark:text-rose-400 tabular-nums">{preview.total - preview.valid}</b> da correggere
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setPreview(null)} data-testid="import-cancel">Annulla</Button>
              <Button size="sm" className="rounded-sm font-semibold" onClick={commit} disabled={busy || preview.valid === 0} data-testid="import-confirm">
                Importa {preview.valid} dipendenti
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto scroll-thin">
            <table className="w-full text-sm">
              <thead className="bg-secondary">
                <tr>
                  {["", "Cognome", "Nome", "Codice", "Reparto", "Mansione", "Assunzione", "Note"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <tr key={r.code || `${r.last_name}-${r.first_name}-${i}`} className="border-t border-border">
                    <td className="px-3 py-1.5">
                      {r.valid ? <CheckCircle size={16} className="text-emerald-600" /> : <XCircle size={16} className="text-rose-600" />}
                    </td>
                    <td className="px-3 py-1.5 font-semibold">{r.last_name}</td>
                    <td className="px-3 py-1.5">{r.first_name}</td>
                    <td className="px-3 py-1.5 tabular-nums">{r.code}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{r.department}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{r.role_title}</td>
                    <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{r.hire_date || "—"}</td>
                    <td className="px-3 py-1.5 text-xs text-rose-600 dark:text-rose-400">{r.errors.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
